import React, { useState } from 'react';
import { X, Pipette } from 'lucide-react';
import { apiPost } from '../../../api/axiosClient';

/**
 * Self-contained modal that lets the user create a new branding template.
 * Uses the same API and form fields as PPTThemePage — no routing needed.
 *
 * Props:
 *   onClose      : () => void         — called to close the modal
 *   onCreated    : (templateId) => void — called after successful save so the
 *                  parent can refresh its template list and pre-select the new one
 */
const CreateThemeModal = ({ onClose, onCreated }) => {
    const [draft, setDraft] = useState({
        name: '',
        clientName: '',
        clientLogoUrl: '',
        clientLogo: null,
        primaryColor: '#FF5526',
        secondaryColor: '#1E293B',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleLogoFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) { alert('File size exceeds 2MB'); return; }
        const reader = new FileReader();
        reader.onloadend = () => setDraft(p => ({ ...p, clientLogo: file, clientLogoUrl: reader.result }));
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        if (!draft.name.trim()) { setError('Theme Name is required.'); return; }
        setSaving(true);
        setError('');
        try {
            const fd = new FormData();
            fd.append('name', draft.name.trim());
            if (draft.clientName) fd.append('client_name', draft.clientName);
            if (draft.clientLogoUrl && !draft.clientLogo) fd.append('logo_url', draft.clientLogoUrl);
            fd.append('primary_color', draft.primaryColor);
            fd.append('secondary_color', draft.secondaryColor);
            if (draft.clientLogo) fd.append('logo_file', draft.clientLogo);

            const res = await apiPost('/templates', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            const newId = res.data?.template_id || res.data?.id || '';
            onCreated?.(newId);
            onClose();
        } catch (err) {
            console.error('Failed to create theme:', err);
            const detail = err?.response?.data?.detail;
            if (Array.isArray(detail)) {
                setError(detail.map(d => d.msg).join('; '));
            } else {
                setError(typeof detail === 'string' ? detail : 'Failed to save theme. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    const ColorField = ({ label, field }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500', color: '#334155' }}>{label}</label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ width: '42px', height: '42px', borderRadius: '6px', background: draft[field], border: '1px solid rgba(0,0,0,0.1)', flexShrink: 0 }} />
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', border: '1px solid #E2E8F0', borderRadius: '6px', overflow: 'hidden', paddingRight: '10px' }}>
                    <input
                        type="text"
                        value={draft[field].toUpperCase()}
                        onChange={(e) => setDraft(p => ({ ...p, [field]: e.target.value }))}
                        style={{ flex: 1, border: 'none', padding: '9px 12px', outline: 'none', fontSize: '13px', fontWeight: '500', color: '#334155', textTransform: 'uppercase' }}
                    />
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <Pipette size={16} color="#64748B" />
                        <input
                            type="color"
                            value={draft[field]}
                            onChange={(e) => setDraft(p => ({ ...p, [field]: e.target.value }))}
                            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        /* Overlay */
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(4px)',
                animation: 'ctm-fade 0.2s ease-out',
            }}
        >
            <style>{`
                @keyframes ctm-fade { from { opacity:0; } to { opacity:1; } }
                @keyframes ctm-slide { from { opacity:0; transform:translateY(16px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
            `}</style>

            {/* Modal panel */}
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto',
                    background: '#FFFFFF', borderRadius: '14px',
                    boxShadow: '0 24px 48px -8px rgba(0,0,0,0.18)',
                    animation: 'ctm-slide 0.28s cubic-bezier(0.16, 1, 0.3, 1)',
                    display: 'flex', flexDirection: 'column',
                }}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #F1F5F9' }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0F172A' }}>Create New Template</h3>
                        <p style={{ margin: '2px 0 0', fontSize: '12.5px', color: '#64748B' }}>Set up branding for your proposal presentation</p>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', display: 'flex', alignItems: 'center', padding: '4px', borderRadius: '6px', transition: 'background 0.15s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#F1F5F9'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

                    {/* Error */}
                    {error && (
                        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '10px 14px', fontSize: '12.5px', color: '#DC2626' }}>
                            {error}
                        </div>
                    )}

                    {/* CLIENT DETAILS section */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>DIVISION DETAILS</span>

                        {/* Theme Name */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '13px', fontWeight: '500', color: '#334155' }}>Theme Name <span style={{ color: '#EF4444' }}>*</span></label>
                            <input
                                type="text"
                                placeholder="e.g. Acme Corp Branding"
                                value={draft.name}
                                onChange={(e) => setDraft(p => ({ ...p, name: e.target.value }))}
                                style={{ padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '13.5px', outline: 'none', color: '#0F172A', transition: 'border-color 0.2s' }}
                                onFocus={(e) => e.target.style.borderColor = '#FF5526'}
                                onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                            />
                        </div>

                        {/* Client Name */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '13px', fontWeight: '500', color: '#334155' }}>Division Name</label>
                            <input
                                type="text"
                                placeholder="e.g. Government Division"
                                value={draft.clientName}
                                onChange={(e) => setDraft(p => ({ ...p, clientName: e.target.value }))}
                                style={{ padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '13.5px', outline: 'none', color: '#0F172A', transition: 'border-color 0.2s' }}
                                onFocus={(e) => e.target.style.borderColor = '#FF5526'}
                                onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                            />
                        </div>

                        {/* Logo */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <label style={{ fontSize: '13px', fontWeight: '500', color: '#334155' }}>Division Logo</label>
                            <div style={{ border: '1px dashed #CBD5E1', borderRadius: '8px', padding: '14px', background: '#F8F9FA' }}>
                                {draft.clientLogoUrl ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <img src={draft.clientLogoUrl} alt="Logo preview" style={{ width: '48px', height: '48px', objectFit: 'contain', border: '1px solid #E2E8F0', borderRadius: '6px', background: 'white' }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontWeight: '500', color: '#0F172A' }}>{draft.clientLogo?.name || 'Linked URL'}</div>
                                            <div style={{ fontSize: '11.5px', color: '#64748B' }}>{draft.clientLogo ? 'PNG, JPG or SVG' : 'Remote image'}</div>
                                        </div>
                                        <button onClick={() => setDraft(p => ({ ...p, clientLogo: null, clientLogoUrl: '' }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8' }}><X size={16} /></button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <input
                                            type="text"
                                            placeholder="https://example.com/logo.png"
                                            value={draft.clientLogoUrl}
                                            onChange={(e) => setDraft(p => ({ ...p, clientLogoUrl: e.target.value, clientLogo: null }))}
                                            style={{ padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: '7px', fontSize: '13px', outline: 'none', color: '#0F172A' }}
                                        />
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{ flex: 1, height: '1px', background: '#E2E8F0' }} />
                                            <span style={{ fontSize: '11px', color: '#94A3B8' }}>or upload file</span>
                                            <div style={{ flex: 1, height: '1px', background: '#E2E8F0' }} />
                                        </div>
                                        <label style={{ display: 'inline-flex', alignSelf: 'center', padding: '6px 18px', border: '1px solid #E2E8F0', borderRadius: '7px', fontSize: '13px', fontWeight: '500', color: '#334155', cursor: 'pointer', background: 'white' }}>
                                            Upload File
                                            <input type="file" accept=".png,.jpg,.jpeg,.svg" style={{ display: 'none' }} onChange={handleLogoFile} />
                                        </label>
                                        <p style={{ margin: 0, fontSize: '11px', color: '#94A3B8', textAlign: 'center' }}>PNG, JPG or SVG — max 2MB</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* BRAND COLORS section */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>BRAND COLORS</span>
                        <ColorField label="Brand Color" field="primaryColor" />
                        <ColorField label="Accent Color (Headline)" field="secondaryColor" />
                    </div>
                </div>

                {/* Footer */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 24px', borderTop: '1px solid #F1F5F9', background: '#F8F9FA', borderRadius: '0 0 14px 14px' }}>
                    <button
                        onClick={onClose}
                        disabled={saving}
                        style={{ padding: '9px 20px', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px', color: '#475569', fontSize: '13.5px', fontWeight: '600', cursor: 'pointer' }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving || !draft.name.trim()}
                        style={{
                            padding: '9px 22px', background: saving ? '#FFB399' : '#FF5526', border: 'none',
                            borderRadius: '8px', color: '#FFFFFF', fontSize: '13.5px', fontWeight: '600',
                            cursor: saving ? 'not-allowed' : 'pointer', transition: 'background 0.2s',
                            opacity: (!draft.name.trim() && !saving) ? 0.5 : 1,
                        }}
                    >
                        {saving ? 'Saving...' : 'Save Template'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateThemeModal;
